/**
 * Dialog / focus-management primitive.
 *
 * Re-registers the Eurus `xModal` and `xFocusElement` Alpine stores from
 * `assets/theme.js` with a corrected focus trap: active at every viewport,
 * listeners removed on teardown, hidden elements excluded as boundaries, and
 * the focusable set re-queried per keystroke rather than captured on open.
 * Correcting the stores covers every overlay in the theme without touching
 * their ~21 call sites.
 *
 * Public API — load-bearing, preserved from the vendor stores:
 *
 *   $store.xModal.activeElement   (string; read by header-search-block.liquid)
 *   $store.xModal.focused         (bool)
 *   $store.xModal.setActiveElement(id)
 *   $store.xModal.focus(container, focusElement)
 *   $store.xModal.removeFocus([elementId])
 *   $store.xFocusElement.trapFocus(container, focusElement)
 *   $store.xFocusElement.focusWhenReady(container, timeout)
 *   $store.xFocusElement.trapFocusWhenReady(container, focusElement, timeout)
 *   $store.xFocusElement.removeTrapFocus(element)
 *
 * Dialog semantics — role, aria-modal, accessible name, inert background,
 * Escape — are opt-in per container via the `data-a11y-dialog` attribute. The
 * focus-trap corrections apply to every caller regardless.
 */
(() => {
  'use strict';

  /** Elements that can hold focus; visibility is filtered by isFocusable(). */
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]'
  ].join(', ');

  /** Resolve a string id or an Element into an Element. */
  const resolveEl = (ref) => {
    if (!ref) return null;
    if (ref instanceof Element) return ref;
    return document.getElementById(String(ref));
  };

  /**
   * True when the element can receive focus right now: connected, enabled, not
   * aria-hidden, non-negative tabindex, outside any inert / aria-hidden
   * subtree, and rendered.
   *
   * getClientRects() covers display:none, descendants of display:none and
   * content inside a closed <details>. It does NOT catch elements that are
   * rendered but translated offscreen or at opacity:0 — those are page-level
   * tab-order problems, fixed by marking the hidden container inert.
   */
  const isFocusable = (el) => {
    if (!el || !el.isConnected) return false;
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;

    const tabindex = el.getAttribute('tabindex');
    if (tabindex !== null && parseInt(tabindex, 10) < 0) return false;

    // Inside an inert or aria-hidden subtree.
    if (el.closest('[inert]')) return false;
    if (el.closest('[aria-hidden="true"]')) return false;

    // Not rendered at all.
    if (!el.getClientRects().length) return false;

    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;

    return true;
  };

  /**
   * True when the element is still rendered. Detects an overlay dismissed by a
   * path that never called removeFocus(), so the trap can release itself.
   */
  const isRendered = (el) => {
    if (!el || !el.isConnected) return false;
    if (!el.getClientRects().length) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.visibility !== 'collapse';
  };

  /** Visible, focusable descendants of `container`, in DOM order. */
  const focusableWithin = (container) => {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable);
  };

  /**
   * Mark everything outside `element` but inside `root` as inert, so neither
   * focus nor assistive tech reaches content the open surface covers.
   *
   * Walks element -> root marking siblings at each level. `aria-hidden` is set
   * alongside `inert` as a Safari < 15.5 fallback, and the return value records
   * exactly which attributes were added so unmarking never strips one that
   * belonged to the page. Elements carrying `data-a11y-dialog-backdrop` are
   * skipped: `inert` blocks pointer events, which would break
   * click-outside-to-close.
   *
   * @param {Element} element the subtree to keep reachable
   * @param {Element} [root=document.body] outermost ancestor to sweep up to
   *   (inclusive). Pass a narrower root to seal levels inside an already-swept
   *   container, e.g. the mobile-nav drawer.
   * @returns {Array<{el: Element, addedInert: boolean, addedAriaHidden: boolean}>}
   */
  const markInertOutside = (element, root = document.body) => {
    const marked = [];
    let node = element;

    while (node && node !== root && node.parentElement) {
      const parent = node.parentElement;

      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sibling.tagName)) continue;

        if (sibling.hasAttribute('data-a11y-dialog-backdrop')) continue;

        const addedInert = !sibling.hasAttribute('inert');
        const addedAriaHidden = sibling.getAttribute('aria-hidden') !== 'true';

        if (addedInert) sibling.setAttribute('inert', '');
        if (addedAriaHidden) sibling.setAttribute('aria-hidden', 'true');
        if (addedInert || addedAriaHidden) marked.push({ el: sibling, addedInert, addedAriaHidden });
      }

      if (parent === root) break;
      node = parent;
    }

    return marked;
  };

  const unmarkInert = (marked) => {
    for (const { el, addedInert, addedAriaHidden } of marked) {
      if (addedInert) el.removeAttribute('inert');
      if (addedAriaHidden) el.removeAttribute('aria-hidden');
    }
  };

  /**
   * Give an opted-in container its dialog semantics and accessible name. Name
   * resolution: an existing aria-labelledby / aria-label, else a heading with
   * text content, else the `data-a11y-dialog` attribute value.
   *
   * @returns {string[]} attribute names added, for revertDialogSemantics()
   */
  const applyDialogSemantics = (container) => {
    const added = [];
    const addAttr = (name, value) => {
      if (container.hasAttribute(name)) return;
      container.setAttribute(name, value);
      added.push(name);
    };

    addAttr('role', 'dialog');
    addAttr('aria-modal', 'true');

    if (!container.hasAttribute('aria-labelledby') && !container.hasAttribute('aria-label')) {
      const heading = container.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]');

      // A visually hidden heading is still a valid label target, so this
      // tests for text content rather than visibility.
      if (heading && heading.textContent.trim()) {
        if (!heading.id) heading.id = `a11y-dialog-title-${Math.random().toString(36).slice(2, 9)}`;
        addAttr('aria-labelledby', heading.id);
      } else {
        const label = container.getAttribute('data-a11y-dialog');
        if (label) addAttr('aria-label', label);
      }
    }

    return added;
  };

  const revertDialogSemantics = (container, added) => {
    for (const name of added) container.removeAttribute(name);
  };

  /** The single active trap. Nested overlays hand off explicitly. */
  let active = null;

  /**
   * Pending rAF handle for a deferred trapFocus, cancelled by removeTrapFocus so
   * an overlay opened and closed within one frame cannot engage a trap after
   * teardown.
   */
  let pendingEngage = null;

  /**
   * Bumped by every explicit trapFocus / removeTrapFocus. trapFocusWhenReady
   * polls across many frames, where an rAF handle exists only between frames —
   * this generation check is what stops a superseded poll.
   */
  let engageGeneration = 0;

  /**
   * Latest trapFocusWhenReady poll token per container. Per container, not
   * global: a component rendering one panel per breakpoint polls both from one
   * open, and a global cancel would let the loser kill the winner.
   */
  const readyTokens = new WeakMap();
  let readySequence = 0;

  const cancelPendingEngage = () => {
    engageGeneration++;
    if (pendingEngage === null) return;
    cancelAnimationFrame(pendingEngage);
    pendingEngage = null;
  };

  const releaseActive = () => {
    if (!active) return;

    document.removeEventListener('keydown', active.onKeydown, true);
    document.removeEventListener('focusin', active.onFocusin, true);
    if (active.observer) active.observer.disconnect();

    if (active.marked) unmarkInert(active.marked);
    if (active.addedSemantics) revertDialogSemantics(active.container, active.addedSemantics);
    if (active.addedContainerTabindex) active.container.removeAttribute('tabindex');

    active = null;
  };

  const engage = (container, elementFocus) => {
    releaseActive();

    const isDialog = container.hasAttribute('data-a11y-dialog');

    const state = {
      container,
      isDialog,
      marked: null,
      addedSemantics: null,
      addedContainerTabindex: false
    };

    /**
     * Wrap Tab at the boundaries. The focusable set is re-queried per keystroke
     * because dialog contents are dynamic (cart lines, live search results,
     * expanding <details>).
     */
    state.onKeydown = (event) => {
      if (!isRendered(container)) {
        releaseActive();
        return;
      }

      // Escape always releases the trap, and asks an opted-in dialog to close.
      // Releasing unconditionally is the safety valve for an overlay whose close
      // path forgets removeFocus().
      if (event.key === 'Escape') {
        if (isDialog) container.dispatchEvent(new CustomEvent('a11y-dialog:close', { bubbles: true }));
        releaseActive();
        return;
      }

      if (event.key !== 'Tab') return;

      const elements = focusableWithin(container);
      if (!elements.length) {
        event.preventDefault();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const target = event.target;

      if (event.shiftKey && (target === first || target === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && target === last) {
        event.preventDefault();
        first.focus();
      }
    };

    /**
     * Pull focus back for every route out other than Tab: click, programmatic
     * focus(), browser-chrome round-trip.
     */
    state.onFocusin = (event) => {
      if (!isRendered(container)) {
        releaseActive();
        return;
      }
      if (container.contains(event.target)) return;
      if (event.target === document.body) return;

      const elements = focusableWithin(container);
      if (elements.length) {
        elements[0].focus();
      } else {
        container.focus();
      }
    };

    if (isDialog) {
      state.addedSemantics = applyDialogSemantics(container);
      state.marked = markInertOutside(container);
    }

    document.addEventListener('keydown', state.onKeydown, true);
    document.addEventListener('focusin', state.onFocusin, true);

    /**
     * Release without waiting for interaction when the overlay is hidden by a
     * path that never called removeFocus(). Critical for opted-in dialogs: a
     * leaked `inert` sweep blocks pointer events too, leaving the page
     * unclickable with no event to recover on. Alpine's x-show toggles inline
     * style, so watching attributes up the ancestor chain is sufficient.
     */
    state.observer = new MutationObserver(() => {
      if (!isRendered(container)) releaseActive();
    });

    for (let node = container; node && node !== document.body; node = node.parentElement) {
      state.observer.observe(node, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'inert']
      });
    }

    active = state;

    // Move focus in: the requested element, else the first visible control,
    // else the container itself.
    const requested = resolveEl(elementFocus);

    if (requested && isFocusable(requested)) {
      requested.focus();
      return;
    }

    const elements = focusableWithin(container);
    if (elements.length) {
      elements[0].focus();
      return;
    }

    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
      state.addedContainerTabindex = true;
    }
    container.focus();
  };

  const registerStores = () => {
    window.Alpine.store('xFocusElement', {
      // Retained for backward compatibility; the trap uses FOCUSABLE_SELECTOR.
      focusableElements: [FOCUSABLE_SELECTOR],
      listeners: {},

      /**
       * Engage the trap on `container` and move focus into it.
       *
       * @param {string|Element} container dialog container (id or element)
       * @param {string|Element} [elementFocus] element to focus on open;
       *   defaults to the first focusable child, then the container itself
       */
      trapFocus(container, elementFocus) {
        const c = resolveEl(container);
        if (!c) return;

        // Defer one frame: callers fire from x-intersect.full or setTimeout(0)
        // as the panel becomes visible, before it has laid out, which would fail
        // every child's getClientRects() test.
        cancelPendingEngage();
        pendingEngage = requestAnimationFrame(() => {
          pendingEngage = null;
          if (!c.isConnected) return;
          engage(c, elementFocus);
        });
      },

      /**
       * Move focus to the first focusable element inside `container` as soon as
       * one exists, polling by frame for content that arrives over ajax or
       * behind a transition.
       *
       * The lighter contract: focus goes in, the background stays live and Tab
       * continues out of the panel normally. Callers wanting the full modal
       * treatment opt into `data-a11y-dialog` and use `trapFocusWhenReady`.
       *
       * @param {string|Element} container
       * @param {number} [timeout=2000] ms to wait for content before giving up
       */
      focusWhenReady(container, timeout = 2000) {
        const c = resolveEl(container);
        if (!c) return;

        const started = performance.now();
        const attempt = () => {
          // The panel closed again while we were waiting.
          if (!c.isConnected || !isRendered(c)) return;

          const [first] = focusableWithin(c);
          if (first) {
            first.focus();
            return;
          }

          if (performance.now() - started < timeout) requestAnimationFrame(attempt);
        };

        requestAnimationFrame(attempt);
      },

      /**
       * Engage the full modal treatment — trap, dialog semantics, inert
       * background, Escape — once `container` is rendered AND holds something
       * focusable. For panels whose contents arrive over ajax, where an
       * immediate `trapFocus` would trap an empty box.
       *
       * The `isRendered` gate is load-bearing, not defensive: a component that
       * renders one panel per breakpoint behind a shared open flag has a
       * `display: none` copy flagged open at any width, and engaging on that one
       * would mark the whole page `inert` behind an invisible dialog — pointer
       * events included, so the page goes dead with no visible cause. Both
       * copies poll concurrently and the hidden one times out, which is why the
       * poll token is per container (`readyTokens`) and this does not call
       * `cancelPendingEngage`.
       *
       * Gives up silently after `timeout` — untrapped beats trapped empty.
       *
       * @param {string|Element} container
       * @param {string|Element|null} [elementFocus] element to focus on engage;
       *   defaults to the first focusable child. Pass the currently focused
       *   element to re-engage a trap without moving focus.
       * @param {number} [timeout=2000] ms to wait for content before giving up
       */
      trapFocusWhenReady(container, elementFocus = null, timeout = 2000) {
        const c = resolveEl(container);
        if (!c) return;

        const token = ++readySequence;
        readyTokens.set(c, token);

        const generation = engageGeneration;
        const started = performance.now();

        const attempt = () => {
          // A newer poll owns this container; it will do the work.
          if (readyTokens.get(c) !== token) return;

          // Superseded by an explicit trapFocus / removeTrapFocus.
          if (generation !== engageGeneration || !c.isConnected) {
            readyTokens.delete(c);
            return;
          }

          if (isRendered(c) && focusableWithin(c).length) {
            readyTokens.delete(c);
            engage(c, elementFocus);
            return;
          }

          if (performance.now() - started < timeout) {
            requestAnimationFrame(attempt);
            return;
          }

          readyTokens.delete(c);
        };

        requestAnimationFrame(attempt);
      },

      /**
       * Release the active trap and, optionally, restore focus.
       *
       * @param {Element|string|null} [elementToFocus] element to restore focus to
       */
      removeTrapFocus(elementToFocus = null) {
        cancelPendingEngage();
        releaseActive();

        const target = resolveEl(elementToFocus);
        if (target && isFocusable(target)) target.focus();
      }
    });

    window.Alpine.store('xModal', {
      activeElement: '',
      focused: false,

      setActiveElement(element) {
        this.activeElement = element;
      },

      focus(container, elementFocus) {
        this.focused = true;
        window.Alpine.store('xFocusElement').trapFocus(container, elementFocus);
      },

      /**
       * @param {string|Element|null} [elementId] element to restore focus to;
       *   falls back to `activeElement`. Named explicitly by
       *   snippets/media-gallery.liquid, which has no activeElement to fall
       *   back on.
       */
      removeFocus(elementId = null) {
        this.focused = false;
        const openedBy = resolveEl(elementId) || resolveEl(this.activeElement);
        window.Alpine.store('xFocusElement').removeTrapFocus(openedBy);
      }
    });
  };

  /**
   * ⚠ Bound to BOTH `alpine:init` and `alpine:initialized`; do not reduce to one.
   *
   * theme.js registers its own `alpine:init` listener from inside a
   * requestAnimationFrame callback, which runs after this file has parsed — so
   * its handler is queued second and its store definitions overwrite ours.
   * `alpine:initialized` fires after every `alpine:init` handler and is what
   * makes this file authoritative; the `alpine:init` pass keeps the corrected
   * stores available to components that touch them during initialization.
   */
  const registerPreservingState = () => {
    const previous = window.Alpine.store('xModal');
    registerStores();

    // Carry over opener state if anything recorded one before we took over.
    if (previous && previous.activeElement) {
      window.Alpine.store('xModal').activeElement = previous.activeElement;
    }
  };

  document.addEventListener('alpine:init', registerPreservingState);
  document.addEventListener('alpine:initialized', registerPreservingState);

  /**
   * Shared surface for the other `custom-a11y-*` modules: the inert sweep and
   * the two visibility predicates, so consumers match the trap's notion of "on
   * screen" and its added-attribute bookkeeping rather than re-implementing it.
   *
   * The trap itself is not published — it is reached through the
   * `$store.xFocusElement` / `$store.xModal` stores.
   */
  window.FarmacyA11y = Object.assign(window.FarmacyA11y || {}, {
    markInertOutside,
    unmarkInert,
    isFocusable,
    isRendered
  });
})();
